
import { useEffect, useRef, useState } from 'react';
import { loadHubSpotFormsScript } from '@/utils/trackingScripts';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface HubSpotFormEmbedProps {
  formId?: string;
  portalId?: string;
  region?: string;
  className?: string;
  onFormSubmitted?: () => void;
  hiddenFields?: Record<string, string>;
  loadingComponent?: React.ReactNode;
}

export const HubSpotFormEmbed = ({
  formId = "1696a603-03f3-486d-aed9-36414a01ba23", // Default to the contact form
  portalId = "3377390",
  region = "na1",
  className = "",
  onFormSubmitted,
  hiddenFields,
  loadingComponent
}: HubSpotFormEmbedProps) => {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [useBackupForm, setUseBackupForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const uniqueId = `hubspot-form-${formId}-${Math.random().toString(36).substring(2, 9)}`;
  const isMobile = useIsMobile();

  useEffect(() => {
    let isMounted = true;
    
    // Create the HubSpot form
    const createForm = () => {
      if (!formContainerRef.current || !window.hbspt?.forms) {
        console.log("Form container not found or HubSpot forms not ready");
        return false;
      }
      
      // Clear any existing content
      formContainerRef.current.innerHTML = '';
      
      try {
        window.hbspt.forms.create({
          region: region,
          portalId: portalId,
          formId: formId,
          target: `#${uniqueId}`,
          onFormSubmitted: () => {
            console.log('HubSpot form submitted via onFormSubmitted callback');
            if (onFormSubmitted && isMounted) {
              onFormSubmitted();
            }
            // Show toast notification on submission
            toast.success("Form submitted successfully!");
          },
          onFormReady: () => {
            console.log(`HubSpot form ${formId} is ready`);
            if (isMounted) {
              setIsLoaded(true);
              setIsLoading(false);
              
              // Apply custom styling to HubSpot form elements
              if (formContainerRef.current) {
                const formElement = formContainerRef.current.querySelector('form');
                if (formElement) {
                  formElement.classList.add('edgefi-hubspot-form');
                }
                
                // Style input fields
                const inputs = formContainerRef.current.querySelectorAll('input, select, textarea');
                inputs.forEach(input => {
                  input.classList.add('edgefi-form-input');
                });
                
                // Style submit button
                const submitButton = formContainerRef.current.querySelector('input[type="submit"]');
                if (submitButton) {
                  submitButton.classList.add('edgefi-form-submit');
                }
              }
            }
          },
          hiddenFields: hiddenFields || {},
        });
        return true;
      } catch (error) {
        console.error("Error creating HubSpot form:", error);
        return false;
      }
    };

    // Initialize the form with retries
    const initForm = async () => {
      try {
        setIsLoading(true);
        
        console.log("Loading HubSpot script...");
        // Load the HubSpot forms script with timeout
        const scriptPromise = loadHubSpotFormsScript();
        
        // Create a timeout promise
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Script loading timed out")), 8000);
        });
        
        // Race between script loading and timeout
        await Promise.race([scriptPromise, timeoutPromise]);
        
        // Give a small delay for initialization
        setTimeout(() => {
          if (isMounted) {
            if (createForm()) {
              console.log(`HubSpot form ${formId} created successfully`);
            } else {
              // Retry form creation if needed (only once)
              setTimeout(() => {
                if (isMounted && !isLoaded) {
                  console.log("Retrying form creation...");
                  if (!createForm() && isMounted) {
                    // If still failing, switch to backup form
                    setUseBackupForm(true);
                    setIsLoading(false);
                  }
                }
              }, 1500);
            }
          }
        }, 500);
      } catch (error) {
        console.error("Error initializing HubSpot form:", error);
        
        if (isMounted) {
          // Update load attempt counter for smart retry
          setLoadAttempt(prev => prev + 1);
          
          // Only fall back to backup form after multiple attempts
          if (loadAttempt >= 2) {
            setUseBackupForm(true);
            setIsLoading(false);
          } else {
            // Try again with a delay
            setTimeout(() => {
              if (isMounted) initForm();
            }, 2000);
          }
        }
      }
    };

    // Add message event listener for HubSpot form submission
    const handleMessage = (event: MessageEvent) => {
      // Check if the message is from HubSpot
      if (event.data && typeof event.data === 'object') {
        // Handle form submission events from HubSpot
        if (
          event.data.type === 'hsFormCallback' && 
          event.data.eventName === 'onFormSubmitted' &&
          event.data.id === formId
        ) {
          console.log('HubSpot form submitted via message event:', event.data);
          
          // Show toast notification
          toast.success("Form submitted successfully!");
          
          // Call the callback if provided
          if (onFormSubmitted && isMounted) {
            onFormSubmitted();
          }
        }
      }
    };

    // Add event listener for HubSpot form submission messages
    window.addEventListener('message', handleMessage);

    // Start the form initialization
    initForm();

    // Clean up
    return () => {
      isMounted = false;
      window.removeEventListener('message', handleMessage);
    };
  }, [formId, portalId, region, uniqueId, hiddenFields, onFormSubmitted, isLoaded, loadAttempt]);

  // Handle direct form submission for the backup form
  const handleBackupFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Get form data
      const formData = new FormData(e.currentTarget);
      const fields: Array<{name: string, value: string}> = [];
      
      // Convert FormData to fields array for HubSpot
      formData.forEach((value, name) => {
        fields.push({
          name,
          value: value.toString()
        });
      });

      // Submit directly to HubSpot
      const hubspotUrl = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;
      
      const payload = {
        fields: fields,
        context: {
          pageUri: window.location.href,
          pageName: document.title
        }
      };
      
      const response = await fetch(hubspotUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      // For development environment, simulate success
      if (import.meta.env.DEV && !response.ok) {
        console.log("Development environment detected - simulating successful submission");
        
        toast.success("Form submitted successfully (Development Mode)");
        
        if (onFormSubmitted) {
          onFormSubmitted();
        }
        
        // Clear form fields
        e.currentTarget.reset();
        setIsSubmitting(false);
        return;
      }
      
      if (response.ok) {
        toast.success("Thank you for your submission. We'll be in touch soon.");
        
        if (onFormSubmitted) {
          onFormSubmitted();
        }
        
        // Clear form fields
        e.currentTarget.reset();
      } else {
        const responseText = await response.text();
        throw new Error(`HubSpot API error: ${responseText}`);
      }
    } catch (error) {
      console.error("Error submitting backup form:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      setSubmitError(errorMessage);
      
      toast.error("Form submission failed. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create a fallback/backup form when HubSpot script fails
  const renderBackupForm = () => {
    return (
      <div className="p-6 border border-purple-300 rounded-md bg-white/70 backdrop-blur-sm shadow-sm hover:shadow-md transition-all">
        <h3 className="text-lg font-medium mb-4">Contact Us</h3>
        
        {submitError && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded-md">
            <p className="text-sm font-medium">Error: {submitError}</p>
            <p className="text-xs mt-1">Please try again or contact us directly at info@edgefi.com</p>
          </div>
        )}
        
        <form 
          className="space-y-4"
          onSubmit={handleBackupFormSubmit}
        >
          <div>
            <label className="block text-sm font-medium mb-1">First Name*</label>
            <input 
              type="text" 
              name="firstname" 
              required 
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Last Name*</label>
            <input 
              type="text" 
              name="lastname" 
              required 
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email*</label>
            <input 
              type="email" 
              name="email" 
              required 
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input 
              type="tel" 
              name="phone" 
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Company</label>
            <input 
              type="text" 
              name="company" 
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea 
              name="message" 
              rows={4} 
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:outline-none"
            ></textarea>
          </div>
          
          {/* Hidden fields */}
          {hiddenFields && Object.entries(hiddenFields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
          
          <button 
            type="submit" 
            className="w-full bg-gradient-to-r from-edgefi-purple to-edgefi-amethyst hover:from-edgefi-purple/90 hover:to-edgefi-amethyst/90 text-white font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 hover:shadow-md transition-all"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : "Submit"}
          </button>
        </form>
      </div>
    );
  };

  return (
    <div className={`hubspot-form-container ${className}`}>
      {isLoading && !useBackupForm && (
        <div className="animate-fade-in py-4">
          {loadingComponent || (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-edgefi-purple/30 border-t-edgefi-purple rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      )}
      
      {useBackupForm ? (
        renderBackupForm()
      ) : (
        <div className="form-wrapper bg-gradient-to-b from-white to-purple-50 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-1">
          <div 
            id={uniqueId} 
            ref={formContainerRef} 
            className={`hs-form-container ${isMobile ? 'max-h-[50vh]' : 'max-h-full'} overflow-y-auto p-5 rounded-lg animate-fade-in`}
          >
            {/* Form will be rendered here by HubSpot */}
          </div>
        </div>
      )}
    </div>
  );
};
